import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useMountEffect } from "@react-hookz/web";
import { isToday, isYesterday } from "date-fns";
import groupBy from "lodash/groupBy";
import React from "react";
import { graphql, readInlineData } from "react-relay";
import { parseRouteType, useSdkContext } from "scmp-sdk/components/contexts";

import type { readingHistoryPanelArticle$key } from "scmp-app/queries/__generated__/readingHistoryPanelArticle.graphql";

import { formatGroupDate } from "./helpers";
import type { ReadingHistoryWithArticle, ReadingHistoryWithArticleState } from "./hooks";
import { useReadingHistoryOperations } from "./hooks";
import { ReadingHistoryActionMenu } from "./reading-history-action-menu";
import {
  Container,
  EmptyContainer,
  HeaderTitle,
  ItemButton,
  ItemTitle,
  Loading,
  PlainTextButton,
  Section,
  SectionHeader,
  SectionItem,
  StyledReadingHistoryEmptyIcon,
  Title,
} from "./styles";

type Props = {
  state: ReadingHistoryWithArticleState;
};
export const ReadingHistoryPanel: React.FunctionComponent<Props> = ({ state }) => {
  const { currentPageType, handleRoutePush, handleTrackGA4, user } = useSdkContext();
  const { clearReadingHistory } = useReadingHistoryOperations();
  const groupedReadingHistory = Object.entries(
    groupBy(state.result, item => formatGroupDate(item.createdAt)),
  );

  useMountEffect(() => {
    handleTrackGA4({
      action: "imp",
      category: "readinghistory",
      subcategory: "",
    });
  });

  return (
    <ReadingHistoryActionMenu>
      {({ bindTrigger, isCurrentItem }) => {
        const renderEmptyMessage = () => {
          const message = user
            ? "Your recently viewed stories will appear here. This allows us to give you a more personalised experience."
            : "By signing up as a registered user, your recently viewed stories will appear here. This allows us to give you a more personalised experience.";
          return (
            <EmptyContainer>
              {message}
              <StyledReadingHistoryEmptyIcon />
            </EmptyContainer>
          );
        };

        const renderList = () => {
          const formatDisplayGroupDate = (groupDate: string) => {
            const date = new Date(groupDate);
            if (isToday(date)) return "Today";
            if (isYesterday(date)) return "Yesterday";
            return groupDate;
          };

          const renderItem = (item: ReadingHistoryWithArticle) => {
            const article = readInlineData<readingHistoryPanelArticle$key>(
              graphql`
                fragment readingHistoryPanelArticle on Article @inline {
                  __typename
                  entityUuid
                  entityId
                  urlAlias
                  headline
                  socialHeadline
                  application {
                    entityUuid
                  }
                }
              `,
              item.article,
            );

            return (
              <SectionItem key={item.entityUuid}>
                <ItemTitle
                  onClick={() => {
                    void handleTrackGA4({
                      action: "click",
                      category: "readinghistory",
                      customized_parameters: {
                        action_type: "read",
                        article_id: article.entityId,
                      },
                      subcategory: "",
                    });

                    void handleRoutePush(article.urlAlias, {
                      applicationId: article.application?.entityUuid,
                      entityId: article.entityId,
                      entityUuid: article.entityUuid,
                      query: {
                        campaign: "reading_history",
                        module: "my_account_overlay",
                        pgtype: currentPageType,
                      },
                      type: parseRouteType(article.__typename),
                    });
                  }}
                >
                  {article.socialHeadline ?? article.headline}
                </ItemTitle>
                <ItemButton
                  aria-label="more action"
                  disableRipple
                  {...bindTrigger(item)}
                  $isActive={isCurrentItem(item)}
                >
                  <MoreHorizIcon color="inherit" />
                </ItemButton>
              </SectionItem>
            );
          };

          return groupedReadingHistory.map(([groupDate, items]) => (
            <Section key={groupDate}>
              <SectionHeader>
                <HeaderTitle>{formatDisplayGroupDate(groupDate)}</HeaderTitle>
                <PlainTextButton onClick={() => void clearReadingHistory(groupDate)}>
                  Clear
                </PlainTextButton>
              </SectionHeader>
              {items.map(item => renderItem(item))}
            </Section>
          ));
        };

        const render = () => {
          const isEmptyReadingHistory = groupedReadingHistory.length === 0;
          switch (state.status) {
            case "error":
            case "success":
              return isEmptyReadingHistory ? renderEmptyMessage() : renderList();
            case "loading":
              if (!isEmptyReadingHistory) {
                return;
              }
              return <Loading color="inherit" />;
            case "not-executed":
              return <Loading color="inherit" />;
          }
        };

        return (
          <Container>
            <Title>Recently Viewed</Title>
            {render()}
          </Container>
        );
      }}
    </ReadingHistoryActionMenu>
  );
};

ReadingHistoryPanel.displayName = "ReadingHistoryPanel";
